import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VolunteersSurnameGroup = () => {
    // --- State ---
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks ---
    const navigate = useNavigate();

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Volunteer's Surname Group",
            filterPlaceholder: "Search by name or mobile...",
            loadingData: "Loading Data...",
            noData: "No volunteers found.",
            call: "Call",
            members: "Members :",
            voter: "Voter :",
            totalSum: "Total Members :"
        },
        hi: {
            pageTitle: "स्वयंसेवक उपनाम समूह",
            filterPlaceholder: "नाम या मोबाइल द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई स्वयंसेवक नहीं मिला।",
            call: "कॉल",
            members: "सदस्य :",
            voter: "मतदाता :",
            totalSum: "कुल सदस्य :"
        },
        gu: {
            pageTitle: "સ્વયંસેવકનું ઉપનામ જૂથ",
            filterPlaceholder: "નામ અથવા મોબાઇલથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ સ્વયંસેવક મળ્યા નથી.",
            call: "કૉલ",
            members: "સભ્યો :",
            voter: "મતદાર :",
            totalSum: "કુલ સભ્યો :"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVolunteers = async () => {
        if (!appId || !userId) return;
        setLoading(true);
        setSearchTerm("");
        try {
            const response = await axios.post(
                `${BASE_URL}/api/display_surname_match_admin`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setVolunteers(response.data.data.result);
            } else {
                setVolunteers([]);
            }
        } catch (error) {
            console.error("Error fetching volunteers:", error);
            setVolunteers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (selectedUserId , userName) => {
        navigate('/dis_vol_surname_group', { state: { userId: selectedUserId , userName:userName } });
    };

    useEffect(() => {
        fetchVolunteers();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredVolunteers = volunteers.filter((vol) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const name = vol.name ? vol.name.toLowerCase() : "";
        const mobile = vol.mobile_no ? String(vol.mobile_no) : "";
        const desig = vol.designation ? vol.designation.toLowerCase() : "";
        return name.includes(term) || mobile.includes(term) || desig.includes(term);
    });

    const totalMemberCount = filteredVolunteers.reduce((sum, vol) => {
        return sum + (parseInt(vol.total_surname_member) || parseInt(vol.total) || 0);
    }, 0);

    return (
        <div className='app-container p-4'>

            {/* Header with empty subtitle */}
            <Header title={txt.pageTitle} subtitle="" />

            {/* Subtitle below header with Gradient Line (Option 7) */}
            {!loading && (
                <div className="subtitle-container mb-4">
                    <div className="subtitle-content">
                        <span className="stats-item">
                            {lang === 'en' ? 'contacts' : 'संपर्क'}
                            <span className="count-badge-stats green">{filteredVolunteers.length}</span>
                            <span className="stats-divider">•</span>
                            {txt.totalSum}
                            <span className="count-badge-stats green">{totalMemberCount}</span>
                        </span>
                    </div>
                </div>
            )}

            {/* --- Styled Professional Search Bar --- */}
            {volunteers.length > 0 && (
                <div className="filter-container-modern mb-4">
                    <div className="search-wrapper-modern">
                        <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input-modern"
                            type="text"
                            placeholder={txt.filterPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {!loading && filteredVolunteers.length === 0 && (
                <div className="text-center text-muted p-5 empty-state">
                    <div className="empty-icon">👥</div>
                    <p>{txt.noData}</p>
                </div>
            )}

            <div className="row g-4 pb-5">
                {!loading && filteredVolunteers.map((vol, index) => {
                    const uniqueKey = vol.user_id ? `vol-${vol.user_id}` : `vol-${index}`;
                    const userTypeLabel = vol.designation || (vol.user_type === 'A' ? 'Admin' : (vol.user_type === 'k' ? 'Karyakarta' : 'User'));
                    const memberCount = vol.total_surname_member || vol.total || 0;

                    let badgeClass = 'badge-blue';
                    if (vol.user_type === 'A' || userTypeLabel.toLowerCase().includes('admin')) badgeClass = 'badge-red';
                    else if (vol.user_type === 'k' || userTypeLabel.toLowerCase().includes('karya')) badgeClass = 'badge-orange';

                    let avatarBg = '#f3f4f6'; let avatarText = '#6b7280';
                    if (badgeClass === 'badge-red') { avatarBg = '#fee2e2'; avatarText = '#dc2626'; }
                    else if (badgeClass === 'badge-orange') { avatarBg = '#fef3c7'; avatarText = '#d97706'; }
                    else { avatarBg = '#e0f2fe'; avatarText = '#0284c7'; }

                    return (
                        <div className="col-12 col-lg-3" key={uniqueKey}>
                            {/* FIX 1: Added h-100 */}
                            <div className="volunteer-card-web clickable-card h-100" onClick={() => handleCardClick(vol.user_id, vol.name)}>
                                
                                {/* FIX 2: Added flex-nowrap and height: 100% */}
                                <div className="row align-items-center flex-nowrap g-3" style={{ height: '100%' }}>
                                    <div className="col-auto">
                                        <div className="card-avatar-wrapper">
                                            {vol.photo_path ? (
                                                <img src={vol.photo_path} alt="user" className="card-avatar-img" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
                                            ) : null}
                                            <div className="card-avatar-placeholder" style={{ display: vol.photo_path ? 'none' : 'flex', backgroundColor: avatarBg, color: avatarText }}>
                                                {vol.name ? vol.name.charAt(0).toUpperCase() : "U"}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* FIX 3: Added minWidth: 0 */}
                                    <div className="col" style={{ minWidth: 0 }}>
                                        <div className="card-middle">
                                            <h3 className="card-title-name text-truncate" title={vol.name}>
                                                {index + 1}. {vol.name || "Unknown"}
                                            </h3>
                                            <div className="card-meta-row d-flex align-items-center flex-wrap gap-2">
                                                <span className="voter-count-label">{txt.voter} <strong>{memberCount}</strong></span>
                                                <span className="divider-dot">•</span>
                                                <span className={`role-badge ${badgeClass}`}>{userTypeLabel}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-auto">
                                        <button className="btn-call-circle" onClick={(e) => { e.stopPropagation(); window.open(`tel:${vol.mobile_no}`); }} disabled={!vol.mobile_no} title={txt.call}>
                                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <style jsx>{`
                /* Subtitle Styles - Option 7: Gradient Line */
                .subtitle-container {
                    margin-top: 8px;
                    margin-bottom: 20px;
                    position: relative;
                }
                .subtitle-content {
                    font-size: 15px;
                    color: #334155;
                    margin: 0 0 10px 0;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .stats-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .stats-divider {
                    color: #cbd5e1;
                    font-size: 16px;
                }
                .count-badge-stats {
                    background: #e2e8f0;
                    color: #475569;
                    padding: 2px 10px;
                    border-radius: 20px;
                    font-weight: 700;
                    font-size: 13px;
                    margin-left: 4px;
                }
                .count-badge-stats.green {
                    background: #dcfce7;
                    color: #166534;
                }
                .subtitle-container::after {
                    content: "";
                    display: block;
                    width: 60px;
                    height: 3px;
                    background: linear-gradient(to right, #2563eb, #60a5fa);
                    border-radius: 3px;
                    margin-top: 4px;
                }

                /* --- MODERN SEARCH BAR STYLES --- */
                .filter-container-modern {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                }
                .search-wrapper-modern {
                    position: relative;
                    width: 100%;
                    max-width: 450px;
                }
                .search-icon-modern {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 20px;
                    height: 20px;
                    color: #94a3b8;
                    pointer-events: none;
                }
                .search-input-modern {
                    width: 100%;
                    padding: 12px 16px 12px 42px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 14px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-input-modern:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                .count-badge { background: #e2e8f0; color: #475569; padding: 2px 8px; border-radius: 12px; font-weight: 700; font-size: 0.85rem; }
                .volunteer-card-web { background: white; border-radius: 12px; padding: 16px; border: 1px solid #e5e7eb; transition: transform 0.2s, box-shadow 0.2s; cursor: pointer; }
                .volunteer-card-web:hover { transform: translateY(-3px); box-shadow: 0 8px 15px -3px rgba(0,0,0,0.1); border-color: #cbd5e1; }
                .card-avatar-wrapper { width: 56px; height: 56px; }
                .card-avatar-img, .card-avatar-placeholder { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
                .card-avatar-placeholder { display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: 700; }
                .card-title-name { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin: 0 0 4px 0; }
                .role-badge { font-size: 0.7rem; font-weight: 700; padding: 3px 8px; border-radius: 4px; color: white; text-transform: uppercase; }
                .badge-red { background-color: #ef4444; }
                .badge-orange { background-color: #f59e0b; }
                .badge-blue { background-color: #3b82f6; }
                .voter-count-label { font-size: 0.85rem; color: #4b5563; font-weight: 500; }
                .btn-call-circle { width: 42px; height: 42px; border-radius: 50%; background-color: #e0f2fe; color: #0284c7; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
                .btn-call-circle:hover { background-color: #0ea5e9; color: white; transform: scale(1.1); }
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner-modern { width: 40px; height: 40px; border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 0.8s ease-in-out infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 640px) {
                    .subtitle-content {
                        font-size: 13px;
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .subtitle-container::after {
                        width: 50px;
                        height: 2px;
                    }
                    .stats-divider {
                        display: none;
                    }
                    .stats-item {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default VolunteersSurnameGroup;